import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface License {
  id: number;
  application_id: number | null;
  user_id: string | null;
  license_key: string;
  product_name: string;
  edition: string;
  status: string;
  source: string | null;
  seat_count: number;
  activation_count: number;
  max_activations: number;
  issued_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface UseMyLicensesResult {
  licenses: License[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useMyLicenses(): UseMyLicensesResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setLicenses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("licenses")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (queryError) throw queryError;
      setLicenses((data as License[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "许可证加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  return { licenses, loading: loading || authLoading, error, reload: load };
}